import { useEffect, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const TICK = { fill: "#d6d5ea", fontSize: 12 };

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatDate(dateStr) {
    if (!dateStr) return "";

    const d = new Date(`${dateStr}T00:00:00`);

    if (Number.isNaN(d.getTime())) {
        return String(dateStr);
    }

    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function normalizeRows(raw) {
    const source = raw ?? {};
    const rows = Array.isArray(source)
        ? source
        : Array.isArray(source.data)
            ? source.data
            : Array.isArray(source.rows)
                ? source.rows
                : Array.isArray(source.items)
                    ? source.items
                    : [];

    return rows.map((row) => {
        const positive = toNumber(row.positive ?? row.thumbs_up ?? row.up, 0);
        const negative = toNumber(row.negative ?? row.thumbs_down ?? row.down, 0);
        const total = toNumber(row.total ?? row.feedback_count, positive + negative);

        return {
            date: row.date ?? row.day ?? row.period ?? row.label ?? "",
            total,
            positive,
            negative,
        };
    });
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;

    return (
        <div className="remediation-dashboard-tooltip">
            <div className="remediation-dashboard-tooltip-title">
                {formatDate(label)}
            </div>

            {payload.map((point) => (
                <div
                    key={point.dataKey}
                    style={{
                        color: point.stroke,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 20,
                    }}
                >
                    <span>{point.name}</span>
                    <strong>{formatInt(point.value)}</strong>
                </div>
            ))}
        </div>
    );
}

export default function FeedbackTrend() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const fetchStats = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=feedback_per_day`
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error("Feedback trend is not available yet.");
                    }

                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizeRows(payload);

                if (!cancelled) {
                    setRows(normalized);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load feedback trend");
                    setRows([]);
                    setLoading(false);
                }
            }
        };

        fetchStats();

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section className="remediation-dashboard-section">
            <article className="remediation-dashboard-card">
                <h3 className="remediation-dashboard-card-title">Feedback per Day</h3>

                {loading && (
                    <div className="remediation-dashboard-loading">
                        <span className="remediation-dashboard-loading-spinner" />
                        <span>Loading data...</span>
                    </div>
                )}

                {!loading && (error || rows.length === 0) && (
                    <div className="remediation-dashboard-empty-state">
                        {error || "No feedback trend data available"}
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <MeasuredChartFrame height={320}>
                        {({ width, height }) => (
                            <LineChart
                                width={width}
                                height={height}
                                data={rows}
                                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="rgba(255,255,255,0.06)"
                                />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                    width={52}
                                    tickFormatter={formatInt}
                                />
                                <Tooltip
                                    cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                                    content={<CustomTooltip />}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: 12,
                                        color: "#d6d5ea",
                                        paddingTop: 8,
                                    }}
                                    formatter={(value) => (
                                        <span style={{ color: "#e5e8f3" }}>{value}</span>
                                    )}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="total"
                                    name="Total"
                                    stroke="#EDEBFF"
                                    strokeWidth={2.4}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="positive"
                                    name="Positive"
                                    stroke="#52EA91"
                                    strokeWidth={2.2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="negative"
                                    name="Negative"
                                    stroke="#EF4444"
                                    strokeWidth={2.2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />
                            </LineChart>
                        )}
                    </MeasuredChartFrame>
                )}
            </article>
        </section>
    );
}